import React, { useEffect, useState } from 'react';
import { AiOutlinePlusCircle } from 'react-icons/ai';
import { IoTrashOutline } from 'react-icons/io5';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Divider, Flex, Icon, IconButton, Tooltip, VStack } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import DropdownMenu from '../../../../../../../components/DropdownMenu';
import { findSelected } from '../../../../../../../components/DropdownMenu/util';
import InputLabel from '../../../../../../../components/form/InputLabel';
import { COMPANY_ADMIN, COMPANY_USER } from '../../../../../../../constants/roles';
import {
  adminPageCreateUserDepartmentRole,
  adminPageDeleteDepartmentRole,
  adminPageDeleteUserCompanyRole,
  adminPageListRoles,
  adminPageNewUserData,
  adminPageUpdateUserCompanyRole,
  selectAdminPageNewUserData,
  selectAdminPageRoles
} from '../../../../../../../redux/modules/adminPage';
import { selectCompanyDepartments } from '../../../../../../../redux/modules/company';
import { hideTray, selectTrayOptions } from '../../../../../../../redux/modules/tray';
import { selectUserId } from '../../../../../../../redux/modules/user';
import { removeArrayItemAt, replaceArrayItemAtIndex } from '../../../../../../../utils/arrayUtil';
import DepartmentRoleBox from '../../DepartmentRoleBox';

const companyRoleTooltip = 'Users assigned a COMPANY ROLE will be automatically assigned with that '
  + 'role to all new departments.';
const departmentRoleTooltip = 'DEPARTMENT ROLES allow you to give a user different permission settings'
  + ' per department. If a user is also assigned a COMPANY ROLE, the DEPARTMENT ROLE will override the company role for the specified departments.';

const UserPermissions = ({ companyId, user, step }) => {
  const [selectedCompanyRole, setCompanyRole] = useState();
  const [selectedDepartmentRoles, setSelectedDepartmentRoles] = useState([]);
  const [stagedDepartmentRole, setStagedDepartmentRole] = useState(null);

  const dispatch = useDispatch();
  const departments = useSelector(selectCompanyDepartments);
  const newUserData = useSelector(selectAdminPageNewUserData);
  const roles = useSelector(selectAdminPageRoles);
  const currentLoggedInUserId = useSelector(selectUserId);

  const userAdminRole = user?.roles.find(role => role.pivot.scopeType === 'company' && role.pivot.scopeId === parseInt(companyId, 10) && role.name === COMPANY_ADMIN);
  const isCurrentUser = user?.userId === currentLoggedInUserId;
  const disabledMessage = isCurrentUser
    ? 'You cannot update your own roles'
    : 'You cannot have a Department role with a Company Admin role.';

  useEffect(() => {
    dispatch(adminPageListRoles(companyId));
  }, [companyId, dispatch]);

  useEffect(() => {
    if (userAdminRole) {
      setStagedDepartmentRole(null);
    }
  }, [userAdminRole]);

  useEffect(() => {
    if (user) {
      setSelectedDepartmentRoles([]);

      const userCompanyRole = user.roles.find(role => role.pivot.scopeType === 'company' && role.pivot.scopeId === parseInt(companyId, 10));
      setCompanyRole(userCompanyRole);
      const userDepartmentRoles = user.roles.filter(role => role.pivot.scopeType === 'department' && departments.find(d => d.departmentId === role.pivot.scopeId));

      if (userDepartmentRoles.length) {
        const displayRoles = {};

        userDepartmentRoles.forEach(role => {
          if (displayRoles[role.id]?.departmentIds.length) {
            displayRoles[role.id].departmentIds.push(role.pivot.scopeId);
          } else {
            displayRoles[role.id] = { departmentIds: [role.pivot.scopeId] };
          }
        });

        const displayRolesArray = Object.keys(displayRoles).map(key => {
          const roleId = Number(key);
          const { departmentIds } = displayRoles[key];
          return { roleId, departmentIds };
        });

        setSelectedDepartmentRoles(displayRolesArray);
      }
    }
  }, [dispatch, user]);

  useEffect(() => {
    if (step === 1 && newUserData.companyRole) {
      const companyRole = findSelected(roles, newUserData.companyRole);
      setCompanyRole(companyRole);
    }

    if (step === 1 && newUserData.roles) {
      setSelectedDepartmentRoles(newUserData.roles);
    }
  }, [newUserData.companyRole, newUserData.roles, roles, step]);

  const handleCompanyRoleChange = async selected => {
    const updatedRole = findSelected(roles, Number(selected.value));
    setCompanyRole(updatedRole);
    dispatch(adminPageNewUserData({ ...newUserData, companyRole: updatedRole.id }));

    if (user) {
      const currentRoleId = user.roles.find(role => role.pivot.scopeType === 'company').id;
      dispatch(adminPageUpdateUserCompanyRole(user.userId, currentRoleId, updatedRole.id));

      if (updatedRole.permissionsCount === 0) {
        dispatch(hideTray());
      }
    }
  };

  const handleDepartmentRoleDelete = (roleId, departmentIds) => {
    const updatedRoles = selectedDepartmentRoles.filter(role => role.roleId !== roleId);

    if (user) {
      dispatch(adminPageDeleteDepartmentRole({
        roleId,
        departmentIds
      }, user.userId));
    } else {
      dispatch(adminPageNewUserData({ ...newUserData, roles: updatedRoles }));
    }
  };

  const handleCompanyRoleDelete = e => {
    const roleId = Number(e.currentTarget.value);
    dispatch(adminPageDeleteUserCompanyRole(user.userId, roleId));
  };

  const stageNewDepartmentRole = () => {
    setStagedDepartmentRole({ roleId: null, departmentIds: [] });
  };

  const addUserDepartmentRole = role => {
    if (user) {
      if (newUserData.roles) {
        dispatch(adminPageCreateUserDepartmentRole([...newUserData.roles, role], user.userId));
      } else {
        dispatch(adminPageCreateUserDepartmentRole([role], user.userId));
      }
    } else {
      dispatch(adminPageNewUserData({
        ...newUserData,
        roles: [...selectedDepartmentRoles, role],
        stagedRole: null
      }));
    }
  };

  const handleDeptRoleChange = role => {
    if (!(role.roleId && role.departmentIds)) return;

    const existingRole = selectedDepartmentRoles.find(r => r.roleId === role.roleId);
    const departmentsToDelete = existingRole?.departmentIds.filter(id => !role.departmentIds.includes(id)) || [];
    const departmentsToAdd = role.departmentIds.filter(id => !existingRole?.departmentIds.includes(id));

    setStagedDepartmentRole(null);

    // If the role was changed, keeping the departmentIds the same
    if (role.departmentIds.length && role.previousRoleId && role.previousRoleId !== role.roleId) {
      const currentIndex = selectedDepartmentRoles.findIndex(r => r.roleId === role.previousRoleId);
      const updatedRoles = replaceArrayItemAtIndex(selectedDepartmentRoles, role, currentIndex);
      setSelectedDepartmentRoles(updatedRoles);
      handleDepartmentRoleDelete(role.previousRoleId, role.departmentIds);
      addUserDepartmentRole(role);
      return;
    }

    // if the role is new, add it
    if (!existingRole && role.departmentIds.length) {
      setSelectedDepartmentRoles([...selectedDepartmentRoles, role]);
      addUserDepartmentRole(role);
      return;
    }

    // Check if we are just updating the staged role
    if (!existingRole && !role.departmentIds.length) {
      setStagedDepartmentRole({
        roleId: role.roleId,
        departmentIds: []
      });

      return;
    }

    // If there are no departmentIds, just delete the role
    if (existingRole && !role.departmentIds.length) {
      setSelectedDepartmentRoles(removeArrayItemAt(selectedDepartmentRoles, 'roleId', role.roleId));
      handleDepartmentRoleDelete(role.roleId, existingRole.departmentIds);
      return;
    }

    // Update the role
    const updatedRoles = selectedDepartmentRoles.map(r => {
      if (r.roleId === role.roleId) {
        return role;
      }

      return r;
    });

    // If there are updates to a role, update the role
    if (departmentsToAdd) {
      addUserDepartmentRole({
        roleId: role.roleId,
        departmentIds: departmentsToAdd
      });
    }

    if (departmentsToDelete) {
      handleDepartmentRoleDelete(role.roleId, departmentsToDelete);
    }

    if (!user) {
      dispatch(adminPageNewUserData({ ...newUserData, roles: updatedRoles }));
    } else {
      setSelectedDepartmentRoles(updatedRoles);
    }
  };

  const availableRoles = roles.filter(r => !selectedDepartmentRoles.find(role => role.roleId === r.id));

  return (
    <VStack>
      <InputLabel labelName="Company Role" toolTip={companyRoleTooltip} />
      <Box bg="primaryScale.50" borderRadius="md" height="98px" mb={9} mt={0} w="100%">
        <Tooltip isDisabled={!isCurrentUser} label={disabledMessage}>
          <Flex alignItems="center" height="100%" justifyContent="center" w="100%">
            <DropdownMenu
              isDisabled={isCurrentUser}
              labelKey="displayName"
              options={roles}
              placeholder="Company roles"
              selected={selectedCompanyRole?.name === COMPANY_USER ? 'Unassigned' : selectedCompanyRole?.displayName}
              onSelect={handleCompanyRoleChange}
            />
            <IconButton
              _focus={{ outline: 'none' }}
              icon={<Icon as={IoTrashOutline} color="greyScale.800" height="24px" w="24px" />}
              isDisabled={isCurrentUser}
              value={selectedCompanyRole?.id}
              variant="textGrey"
              onClick={handleCompanyRoleDelete}
            />
          </Flex>
        </Tooltip>
      </Box>
      <Divider mb={9} />
      <InputLabel labelName="Department Role(s)" toolTip={departmentRoleTooltip} />
      {(selectedDepartmentRoles.length > 0 && roles.length > 0)
        && selectedDepartmentRoles.map(deptRole => (
          <DepartmentRoleBox
            availableRoles={availableRoles}
            key={deptRole.roleId}
            role={deptRole}
            user={user || newUserData}
            onChange={handleDeptRoleChange}
            onDelete={() => handleDeptRoleChange({ roleId: deptRole.roleId, departmentIds: [] })}
          />
        ))}

      {!stagedDepartmentRole && (
        <Tooltip isDisabled={!userAdminRole} label={disabledMessage}>
          <Button
            alignItems="center"
            fontSize="md"
            isDisabled={userAdminRole}
            variant="textSuccess"
            w="100%"
            onClick={stageNewDepartmentRole}
          >
            <Icon as={AiOutlinePlusCircle} mr={2} />
            {
              selectedDepartmentRoles.length > 0
                ? 'Add another department role'
                : 'Add department role'
            }
          </Button>
        </Tooltip>
      )}

      {stagedDepartmentRole && (
        <DepartmentRoleBox
          availableRoles={availableRoles}
          role={stagedDepartmentRole}
          user={user || newUserData}
          onChange={handleDeptRoleChange}
          onDelete={() => setStagedDepartmentRole(null)}
        />
      )}
    </VStack>
  );
};

UserPermissions.propTypes = {
  companyId: PropTypes.number.isRequired,
  user: PropTypes.shape({
    userId: PropTypes.number,
    roles: PropTypes.arrayOf(PropTypes.shape({
      roleId: PropTypes.number,
      displayName: PropTypes.string
    }))
  }),
  step: PropTypes.number
};

export default UserPermissions;
